import {
    Checkbox,
    Select,
    Textarea,
    TextInput,
} from '@mantine/core';
import { Dispatch, SetStateAction } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import sharedMessages from 'Components/utils/sharedMessages';
import {
    CONTACT_PERSON_DESCRIPTION_MAX_LENGTH,
    CONTACT_PERSON_TEXT_MAX_LENGTH,
    CONTACT_ROLE_MAX_LENGTH,
    sanitizePhoneNumber,
} from 'utils/validationUtils';

import { LocalCategory, PersonFormState } from '../../../types';
import styles from '../../../InitialSetupModal.module.scss';

type Props = {
    personForm: PersonFormState;
    categories: LocalCategory[];
    isEmailValid: boolean;
    isPhoneValid: boolean;
    labels: {
        personCategory: MessageDescriptor;
        selectCategory: MessageDescriptor;
        firstName: MessageDescriptor;
        lastName: MessageDescriptor;
        role: MessageDescriptor;
        availability: MessageDescriptor;
        description: MessageDescriptor;
        visibleForGuests: MessageDescriptor;
    };
    setPersonForm: Dispatch<SetStateAction<PersonFormState>>;
};

const PersonFormFields = ({
    personForm,
    categories,
    isEmailValid,
    isPhoneValid,
    labels,
    setPersonForm,
}: Props) => {
    const intl = useIntl();

    return (
        <>
            <Select
                id="initial-setup-person-category"
                classNames={{
                    root: styles.personFormField,
                    label: styles.setupFieldLabel,
                    input: styles.setupFieldInput,
                }}
                label={`${intl.formatMessage(labels.personCategory)} *`}
                placeholder={intl.formatMessage(labels.selectCategory)}
                data={categories.map((category) => ({ value: category.id, label: category.name }))}
                value={personForm.categoryId || null}
                onChange={(value) => {
                    setPersonForm((prev) => ({ ...prev, categoryId: value ?? '' }));
                }}
                size="md"
                allowDeselect={false}
                comboboxProps={{ withinPortal: false }}
            />

            <TextInput
                id="initial-setup-person-first-name"
                classNames={{
                    root: styles.personFormField,
                    label: styles.setupFieldLabel,
                    input: styles.setupFieldInput,
                }}
                label={`${intl.formatMessage(labels.firstName)} *`}
                value={personForm.firstName}
                error={personForm.firstName.trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH ? intl.formatMessage(messages.textTooLong, { max: CONTACT_PERSON_TEXT_MAX_LENGTH }) : undefined}
                onChange={(event) => {
                    setPersonForm((prev) => ({ ...prev, firstName: event.target.value }));
                }}
                size="md"
                maxLength={CONTACT_PERSON_TEXT_MAX_LENGTH + 1}
            />
            <TextInput
                id="initial-setup-person-last-name"
                classNames={{
                    root: styles.personFormField,
                    label: styles.setupFieldLabel,
                    input: styles.setupFieldInput,
                }}
                label={`${intl.formatMessage(labels.lastName)} *`}
                value={personForm.lastName}
                error={personForm.lastName.trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH ? intl.formatMessage(messages.textTooLong, { max: CONTACT_PERSON_TEXT_MAX_LENGTH }) : undefined}
                onChange={(event) => {
                    setPersonForm((prev) => ({ ...prev, lastName: event.target.value }));
                }}
                size="md"
                maxLength={CONTACT_PERSON_TEXT_MAX_LENGTH + 1}
            />
            <TextInput
                id="initial-setup-person-role"
                classNames={{
                    root: styles.personFormField,
                    label: styles.setupFieldLabel,
                    input: styles.setupFieldInput,
                }}
                label={`${intl.formatMessage(labels.role)} *`}
                value={personForm.role}
                onChange={(event) => {
                    setPersonForm((prev) => ({ ...prev, role: event.target.value }));
                }}
                error={personForm.role.trim().length > CONTACT_ROLE_MAX_LENGTH ? intl.formatMessage(messages.roleTooLong, { max: CONTACT_ROLE_MAX_LENGTH }) : undefined}
                size="md"
                maxLength={CONTACT_ROLE_MAX_LENGTH + 1}
            />
            <TextInput
                id="initial-setup-person-availability"
                classNames={{
                    root: styles.personFormField,
                    label: styles.setupFieldLabel,
                    input: styles.setupFieldInput,
                }}
                label={intl.formatMessage(labels.availability)}
                value={personForm.availability}
                error={personForm.availability.trim().length > CONTACT_PERSON_TEXT_MAX_LENGTH ? intl.formatMessage(messages.textTooLong, { max: CONTACT_PERSON_TEXT_MAX_LENGTH }) : undefined}
                onChange={(event) => {
                    setPersonForm((prev) => ({ ...prev, availability: event.target.value }));
                }}
                size="md"
                maxLength={CONTACT_PERSON_TEXT_MAX_LENGTH + 1}
            />
            <TextInput
                id="initial-setup-person-phone"
                classNames={{
                    root: styles.personFormField,
                    label: styles.setupFieldLabel,
                    input: styles.setupFieldInput,
                }}
                label={intl.formatMessage(sharedMessages.phoneNumber)}
                value={personForm.phoneNumber}
                error={!isPhoneValid ? intl.formatMessage(messages.invalidPhoneNumber) : undefined}
                onChange={(event) => {
                    setPersonForm((prev) => ({ ...prev, phoneNumber: sanitizePhoneNumber(event.target.value) }));
                }}
                size="md"
            />
            <TextInput
                id="initial-setup-person-email"
                classNames={{
                    root: styles.personFormField,
                    label: styles.setupFieldLabel,
                    input: styles.setupFieldInput,
                }}
                label={intl.formatMessage(sharedMessages.email)}
                value={personForm.email}
                error={!isEmailValid ? intl.formatMessage(sharedMessages.incorrectEmail) : undefined}
                onChange={(event) => {
                    setPersonForm((prev) => ({ ...prev, email: event.target.value }));
                }}
                size="md"
            />
            <Textarea
                id="initial-setup-person-description"
                classNames={{
                    root: styles.personFormField,
                    label: styles.setupFieldLabel,
                    input: styles.setupFieldTextarea,
                }}
                label={intl.formatMessage(labels.description)}
                value={personForm.description}
                error={personForm.description.trim().length > CONTACT_PERSON_DESCRIPTION_MAX_LENGTH ? intl.formatMessage(messages.descriptionTooLong, { max: CONTACT_PERSON_DESCRIPTION_MAX_LENGTH }) : undefined}
                onChange={(event) => {
                    setPersonForm((prev) => ({ ...prev, description: event.target.value }));
                }}
                size="md"
                maxLength={CONTACT_PERSON_DESCRIPTION_MAX_LENGTH + 1}
                rows={3}
            />
            <Checkbox
                id="initial-setup-person-visible-for-guests"
                classNames={{
                    label: styles.setupCheckboxLabel,
                    input: styles.setupCheckboxInput,
                }}
                label={intl.formatMessage(labels.visibleForGuests)}
                checked={personForm.visibleForGuests}
                onChange={(event) => {
                    setPersonForm((prev) => ({ ...prev, visibleForGuests: event.currentTarget.checked }));
                }}
            />
        </>
    );
};

export default PersonFormFields;

const messages = {
    invalidPhoneNumber: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.PeopleStep.PersonFormFields.invalidPhoneNumber',
        defaultMessage: 'Podaj numer telefonu w formacie międzynarodowym, np. +48123456789.',
    },
    roleTooLong: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.PeopleStep.PersonFormFields.roleTooLong',
        defaultMessage: 'Nazwa może mieć maksymalnie {max} znaków.',
    },
    textTooLong: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.PeopleStep.PersonFormFields.textTooLong',
        defaultMessage: 'Pole może mieć maksymalnie {max} znaków.',
    },
    descriptionTooLong: {
        id: 'src.Screens.Dashboard.InitialSetup.steps.PeopleStep.PersonFormFields.descriptionTooLong',
        defaultMessage: 'Opis może mieć maksymalnie {max} znaków.',
    },
};
